import { Button, Card, message, Table, Tooltip, Typography } from 'antd';
import React, { useState, useEffect } from 'react';
import TestLogsCardStyles from "../../styles/TestLogsCardStyles";

const { Text } = Typography;


// Inject styles into the document
if (typeof document !== 'undefined' && !document.querySelector('#expandable-animations')) {
  const styleElement = document.createElement('style');
  styleElement.id = 'expandable-animations';
  styleElement.innerHTML = TestLogsCardStyles.animationStyles;
  document.head.appendChild(styleElement);
}

const TestLogsCard = ({
  logs,
  hasBasketItems,
  details,
  hasSignups,
  testTag,
  loading,
  type,
  retailIds,
  basicTagActivityData,
  persistedTagActivityData,
  generatedTag,
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = React.useState([]);

  const handleExpand = (expanded, record) => {
    if (logs.indexOf(record) !== 0) return;
    setExpandedRowKeys(expanded ? [record.key || 0] : []);
  };

  const hasRetailIdData = () => {
    const retailIdPersisted = generatedTag?.latestRetailIds;
    return retailIdPersisted && retailIdPersisted.length > 0;
  };

  const expandedRowRender = (record) => {
    const retailIdPersisted = generatedTag?.latestRetailIds?.map((item, index) => ({
      id: index.toString(),
      retailId: String(item.retailId || item.retailID || ''),
      pageViews: Number(item.pageViews || item.pageviews || 0),
      signups: Number(item.signups || 0),
      transactions: Number(item.transactions || 0),
      total: Number(item.total || 0),
      sessions: Number(item.sessions || 0),
    })) || [];
    
    if (!retailIdPersisted.length) {
      return (
        <div style={TestLogsCardStyles.noDataContainer}>
          <Text type="secondary">No retail ID activity data available. Click "Test Signal" to fetch data.</Text>
        </div>
      );
    }

    const eventColumns = [
      {
        title: 'Retail ID',
        dataIndex: 'retailId',
        key: 'retailId',
        width: 120,
        render: (retailId) => (
          <Text style={TestLogsCardStyles.retailIdText}>
            {retailId}
          </Text>
        )
      },
      {
        title: 'Page Views',
        dataIndex: 'pageViews',
        key: 'pageViews',
        width: 100,
        render: (pageViews) => (
          <Text style={TestLogsCardStyles.metricText}>
            {pageViews}
          </Text>
        )
      },
      {
        title: 'Transactions',
        dataIndex: 'transactions',
        key: 'transactions',
        width: 100,
        render: (transactions) => (
          <Text style={TestLogsCardStyles.metricText}>
            {transactions}
          </Text>
        )
      },
      {
        title: 'Signups',
        dataIndex: 'signups',
        key: 'signups',
        width: 80,
        render: (signups) => (
          <Text style={TestLogsCardStyles.metricText}>
            {signups}
          </Text>
        )
      },
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        width: 80,
        render: (total) => (
          <Text style={TestLogsCardStyles.totalText}>
            {total}
          </Text>
        )
      },
      {
        title: 'Sessions',
        dataIndex: 'sessions',
        key: 'sessions',
        width: 80,
        render: (sessions) => (
          <Text style={TestLogsCardStyles.metricText}>
            {sessions}
          </Text>
        )
      }
    ];

    return (
      <div
        className="expandable-row-content"
        style={TestLogsCardStyles.expandableRowContent}
      >
        <div style={TestLogsCardStyles.tableContainer}>
          {/* Events Table Section */}
          <div style={TestLogsCardStyles.fadeInUpContainer}>
            <div style={TestLogsCardStyles.slideInTableContainer}>
              <Table
                dataSource={retailIdPersisted}
                columns={eventColumns}
                pagination={false}
                size="small"
                rowKey="id"
                showHeader={true}
                bordered={false}
                style={TestLogsCardStyles.innerTable}
                className="minimalistic-table"
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
  ];

  if (type === 'measurement') {
    columns.push({
      title: 'Transactions',
      dataIndex: 'transactions',
      key: 'transactions',
    });

    columns.push({
      title: 'Page Views',
      dataIndex: 'pageViews',
      key: 'pageViews',
    });

    columns.push({
      title: 'Transaction Total',
      dataIndex: 'total',
      key: 'total',
    });

    columns.push({
      title: 'Sessions',
      dataIndex: 'sessions',
      key: 'sessions',
    });

    if (hasBasketItems) {
      columns.push({
        title: 'Basket Items',
        dataIndex: 'basketItems',
        key: 'basketItems',
      });
    }

    if (hasSignups) {
      columns.push({
        title: 'Sign Ups',
        dataIndex: 'signups',
        key: 'signups',
      });
    }
  } else if (type === 'publisher') {
    columns.push({
      title: 'Page Views',
      dataIndex: 'pageViews',
      key: 'pageViews',
    });
  } else if (type === 'impression') {
    columns.push({
      title: 'Impressions',
      dataIndex: 'impressions',
      key: 'impressions',
    });
  }

  return (
    <Card
      title="TEST LOG"
      style={TestLogsCardStyles.mainCard}
      headStyle={TestLogsCardStyles.mainCardHead}
      extra={
        <>
          <Tooltip title="Note: Test Signal logs data from the past 5 days only">
            <Button
              shape={'round'}
              loading={loading}
              type="primary"
              onClick={async () => {
                const validRetailIds = retailIds.filter(retailId => retailId && retailId.trim() !== '');
                const concatenated = validRetailIds.length > 0 ? validRetailIds.join(',') : null;
                message.info(`Testing Signal for: ${concatenated || 'all retail locations'}`);
                await testTag(concatenated);
              }}
            >
              Test Signal
            </Button>
          </Tooltip>
        </>
      }
    >
      <Table
        key={basicTagActivityData ? 'with-data' : 'without-data'}
        dataSource={logs}
        columns={columns}
        scroll={{ x: "max-content" }}
        size="middle"
        style={TestLogsCardStyles.mainTable}
        {...(hasRetailIdData() && {
          expandable: {
            expandedRowRender,
            expandedRowKeys,
            onExpand: handleExpand,
            rowExpandable: (record) => {
              return logs.indexOf(record) === 0;
            },
          }
        })}
        rowKey={(record, index) => record.key || index}
      />
    </Card>
  );
};

export default TestLogsCard;
